<script setup lang="ts">
import type { IconName } from '~/components/ui/Icon.vue';

// Components
import ByZoneFilter from './zone.vue';
import dayjs from 'dayjs';
import type { EnergyTypes } from '~/types/new/energy';
import type { ModelValueDateRange } from '~/components/nrjx/input/dateRangeWithShortcut/index.vue';
import ComparisonFilter from './comparison.vue';

// Composables
const route = useRoute();
const { formatNumberToIsoNumber } = useNumbers();
const { getEnergyTypes } = useExploreFilter();
const { getCo2byKWhByMeterId, getCO2Formated } = useCO2();
const { setKeysInForage } = useExploreStorage();
const { loadChronologicalData, loadComparisonData } = useChronologicalService();
const { loadZoneData } = useZoneService();
const { getSelectedSite } = globalStore();
const { getSite } = siteStore();

// Stores
const { consumptionResponse, areasToCompare, comparisonResponse } = storeToRefs(chronologicalStore());
const { setAreasToCompare } = chronologicalStore();
const { zoneResponse } = storeToRefs(zoneStore());
const { energyType, period, areaId, getMeterId, aggregationLevel, showTemperature } = storeToRefs(exploreStore());
const { setEnergyType, setPeriod, setAreaId, setAggregationLevel } = exploreStore();

// Computed
const getCo2byKWh = computed(() => getCo2byKWhByMeterId(getMeterId.value));

const zoneConsumption = computed(() => zoneResponse.value?.total_consumption?.quantity ?? 0);
const comparisonConsumption = computed(() => comparisonResponse.value?.reference?.total_consumption?.quantity ?? 0);
const zoneCost = computed(() => Number(zoneResponse.value?.total_consumption?.cost ?? 0));
const chronologicalConsumption = computed(() => Number(consumptionResponse.value?.total_consumption?.quantity ?? 0));
const chronologicalCost = computed(() => Number(consumptionResponse.value?.total_consumption?.cost ?? 0));
const asWeatherStation = computed(() => {
  const siteId = getSelectedSite[0];
  if (!siteId) return false;
  const site = getSite(siteId);
  if (!site) return false;
  return site.weather_station_id !== null;
});

const getEnergyTypeIcon = computed(() => {
  switch (energyType.value) {
    case 'Electricity':
      return 'Zap';
    case 'Gas':
      return 'Flame';
    case 'Water':
      return 'Droplets';
    default:
      return 'Atom';
  }
});

const kpis = computed((): { icon: IconName; kpi: string }[] => {
  let cost: string;
  let consumption: string;
  let co2: string;

  if (route.name === 'explore-by-zone') {
    cost = formatNumberToIsoNumber(zoneCost.value);
    consumption = formatNumberToIsoNumber(zoneConsumption.value);
    co2 = getCO2Formated(zoneConsumption.value, getCo2byKWh.value);
  } else if (route.name === 'explore-chronological' && areasToCompare.value.length > 0) {
    cost = formatNumberToIsoNumber(comparisonConsumption.value);
    consumption = formatNumberToIsoNumber(comparisonConsumption.value);
    co2 = getCO2Formated(comparisonConsumption.value, getCo2byKWh.value);
  } else {
    cost = formatNumberToIsoNumber(chronologicalCost.value);
    consumption = formatNumberToIsoNumber(chronologicalConsumption.value);
    co2 = getCO2Formated(chronologicalConsumption.value, getCo2byKWh.value);
  }

  return [
    {
      icon: 'Gauge',
      kpi: `${consumption} kWh`,
    },
    {
      icon: 'Euro',
      kpi: `${cost} €`,
    },
    {
      icon: 'Earth',
      kpi: co2,
    },
  ];
});

const weekStart = computed(() => {
  const siteId = getSelectedSite[0];
  return siteId !== undefined ? (getSite(siteId)?.production_start_weekday ?? 'monday') : 'monday';
});

// Methods
const setArea = (areaId: number) => {
  setAreaId(areaId);
};

const updateEnergyType = async (energyType: EnergyTypes) => {
  if (getEnergyTypes.value.find((type) => type.key === energyType)?.areaToRedirect) {
    setArea(getEnergyTypes.value.find((type) => type.key === energyType)?.areaToRedirect);
    setEnergyType(energyType);
  } else {
    setEnergyType(energyType);
  }
  setAreasToCompare([]);
  loadData();
};

const updateArea = async (areaId: number) => {
  setArea(areaId);
  setAreasToCompare([]);
  loadData();
};

const updatePeriod = async (event: ModelValueDateRange) => {
  setPeriod({
    start: dayjs(event.start).format('YYYY-MM-DD HH:mm'),
    end: dayjs(event.end).format('YYYY-MM-DD HH:mm'),
  });
  setAggregationLevel(event.aggregationLevel);
  if (areasToCompare.value.length > 0) {
    const newAreasToCompare = areasToCompare.value.map((area) => ({
      ...area,
      start: dayjs(event.start).format('YYYY-MM-DD HH:mm'),
    }));
    setAreasToCompare(newAreasToCompare);
  }
  loadData();
};

const loadData = async () => {
  await setKeysInForage();
  if (route.name === 'explore-chronological' && areasToCompare.value.length === 0) loadChronologicalData();
  else if (route.name === 'explore-chronological' && areasToCompare.value.length > 0) loadComparisonData();
  else if (route.name === 'explore-by-zone') loadZoneData();
};
</script>
<template>
  <div class="flex flex-col bg-white border-r border-gray-200">
    <div class="p-4 grid border-b gap-2 border-gray-200">
      <nrjx-button-group
        :model-value="route.name?.toString() ?? ''"
        :items="[
          { key: 'explore-by-zone', icon: 'Network', tooltip: $t('explore.filters.chart_type.by_zone') },
          { key: 'explore-chronological', icon: 'LineChart', tooltip: $t('explore.filters.chart_type.chronological') },
        ]"
        arrow
        class="mb-4"
        @update:model-value="navigateTo({ name: $event })"
      />

      <nrjx-input-select
        name="energy-type"
        :model-value="energyType"
        :leading-icon="getEnergyTypeIcon"
        item-key="key"
        item-label="label"
        fit-to-select
        item-secondary-label="secondaryLabel"
        :items="getEnergyTypes ?? []"
        :disabled="getEnergyTypes?.length < 1"
        :content="{ direction: 'bottom', align: 'start', sideOffset: 4 }"
        @update:model-value="updateEnergyType($event as EnergyTypes)"
      />
      <nrjx-input-area
        name="area"
        :area-id="areaId"
        :energy-type="energyType"
        class="w-full"
        side="left"
        @update:area-id="updateArea($event)"
      />

      <nrjx-input-date-range-with-shortcut
        name="period"
        leading-icon="Calendar"
        :trailing-icon="undefined"
        date-format="DD/MM/YYYY"
        :model-value="{
          start: dayjs(period.start).toISOString(),
          end: dayjs(period.end).toISOString(),
          aggregationLevel: route.name === 'explore-chronological' ? aggregationLevel : 'day',
        }"
        :week-start="weekStart"
        :hide-aggregation-level="route.name === 'explore-chronological' ? false : true"
        @update:model-value="
          updatePeriod({
            start: dayjs($event?.start).toISOString(),
            end: dayjs($event?.end).toISOString(),
            aggregationLevel: $event?.aggregationLevel,
          })
        "
      />

      <nrjx-input-switch
        v-if="route.name === 'explore-chronological' && aggregationLevel === 'day' && asWeatherStation"
        v-model="showTemperature"
        class="mt-2"
        name="show-temperature"
        :label="$t('explore.filters.show_temperature')"
        @update:model-value="loadData()"
      />

      <div class="flex flex-col gap-2 justify-between items-center w-full transition-all duration-300 pl-4 border-l border-gray-200 mt-4">
        <div
          v-for="kpi in kpis"
          :key="kpi.icon"
          class="bg-gray-50 border border-gray-200 h-[32px] w-full rounded-md flex items-center justify-start gap-2 px-2"
        >
          <div class="h-6 w-6 flex items-center justify-center bg-primary-100 rounded-md">
            <ui-icon :name="kpi.icon" class="h-4 stroke-emerald-600" />
          </div>
          <p class="text-sm font-semibold text-gray-600">{{ kpi.kpi }}</p>
        </div>
      </div>
    </div>
    <div class="flex flex-col flex-1 gap-4 p-4">
      <div :key="$route.path" class="flex flex-col flex-1">
        <comparison-filter v-if="route.name === 'explore-chronological'" />
        <by-zone-filter v-else-if="route.name === 'explore-by-zone'" />
      </div>
    </div>
  </div>
</template>
